import React from "react";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  campaignName: string;
  isDraft: boolean;
  title?: string;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  campaignName,
  isDraft,
  title,
  description,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  isDeleting = false,
}) => {
  if (!isOpen) return null;

  const resolvedTitle = title ?? `Delete this ${isDraft ? "draft" : "campaign"}?`;
  const resolvedDescription =
    description ??
    (isDraft ? (
      <>
        This will permanently delete your draft{" "}
        <span className="text-white/80">{campaignName}</span> and its settings
        from your account. You won&apos;t be able to restore it after deletion.
      </>
    ) : (
      <>
        This will permanently remove the campaign and its settings from your
        account. You won&apos;t be able to restore it after deletion.
      </>
    ));
  const resolvedConfirmText =
    confirmText ?? `Delete ${isDraft ? "draft" : "campaign"}`;
  const resolvedCancelText = cancelText ?? "Cancel";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4 sm:px-6 py-3 sm:py-4">
      {/* Modal Card */}
      <div className="relative w-full max-w-[400px] sm:max-w-[540px] rounded-2xl overflow-hidden bg-[#1a1a1a]">
        {/* Red gradient background top-center */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[180px] sm:h-[220px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top center, rgba(200, 30, 30, 0.65) 0%, transparent 70%)",
          }}
        />

        {/* Content */}
        <div className="relative z-10 p-5 sm:p-8 md:p-10">
          {/* Close button */}
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="absolute top-4 right-4 sm:top-5 sm:right-5 text-white/50 hover:text-white transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {/* Title */}
          <h2 className="text-white text-xl sm:text-2xl md:text-[28px] font-bold leading-[130%] mb-3">
            {resolvedTitle}
          </h2>

          {/* Description */}
          <p className="text-white/60 text-xs sm:text-sm md:text-base leading-[160%] mb-6 sm:mb-8 max-w-[100%] sm:max-w-[420px]">
            {resolvedDescription}
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-3 w-full sm:w-auto">
            {/* Delete Button */}
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="w-full sm:w-auto px-6 py-2.5 rounded-full font-semibold text-white text-sm sm:text-base transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                background: "linear-gradient(135deg, #e53535 0%, #c62020 100%)",
              }}
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin flex-shrink-0" width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                    <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Deleting...
                </>
              ) : (
                resolvedConfirmText
              )}
            </button>

            {/* Cancel Button */}
            <button
              onClick={onCancel}
              disabled={isDeleting}
              className="w-full sm:w-auto px-6 py-2.5 rounded-full font-semibold text-white text-sm sm:text-base border border-white/25 hover:border-white/50 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resolvedCancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
